import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import logo from "../../assets/logo512.png";
import { supabase } from "../../common/supabase";
import {
  getAccountPath,
  getChatPath,
  getDeveloperPath,
  getMemoriesPath,
} from "../../Router/routes";
import { State } from "../../state/state";
import styles from "./AppLayout.stylesheet.module.scss";

const initialsFor = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | undefined,
) => {
  const initials = `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.trim();
  return (initials || email?.[0] || "R").toUpperCase();
};

export const AppLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: State) => state.user.user);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isMenuOpen]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    dispatch({ type: "CLEAR_REDUX_STATE" });
    navigate("/login", { replace: true });
  };

  return (
    <div className={styles.webAppShell}>
      <header className={styles.webAppHeader}>
        <Link to={getMemoriesPath()} className={styles.webAppBrand}>
          <img src={logo} alt="Replay" />
        </Link>
        <div className={styles.webAppHeaderActions}>
          <nav className={styles.webAppNav} aria-label="App navigation">
            <NavLink to={getMemoriesPath()}>Memories</NavLink>
            <NavLink to={getChatPath()}>Chat</NavLink>
          </nav>
          <div className={styles.profileMenu} ref={menuRef}>
            <button
              className={styles.profileTrigger}
              type="button"
              aria-label="Open profile menu"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              {initialsFor(user?.first_name, user?.last_name, user?.email)}
            </button>
            {isMenuOpen && (
              <div className={styles.profileMenuCard}>
                <div className={styles.profileMenuUser}>
                  <strong>
                    {[user?.first_name, user?.last_name]
                      .filter(Boolean)
                      .join(" ") || "Replay User"}
                  </strong>
                  <span>{user?.email}</span>
                </div>
                <Link
                  to={getAccountPath()}
                  className={styles.profileMenuItem}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Account
                </Link>
                <Link
                  to={getDeveloperPath()}
                  className={styles.profileMenuItem}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Developer
                </Link>
                <button
                  className={classNames(
                    styles.profileMenuItem,
                    styles.profileMenuDanger,
                  )}
                  type="button"
                  onClick={handleSignOut}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <Outlet />
    </div>
  );
};
